// Croqui: `components/Icon.tsx`. Os 38 glifos do canvas, agora Phosphor (mesmo módulo de
// `components/icons/`): um nome daqui e o ícone equivalente de lá são o MESMO desenho. A API não mudou.
//
// Aqui ficam os ícones que os componentes do kit recebem por nome (`Btn`, `Stat`, `Empty`,
// `Notice`, `IconTile`).

import { CoinsIcon } from '@phosphor-icons/react';
import { cn } from '../../lib/cn.ts';
import {
  type AppIcon,
  ArrowLeftRight,
  Bell,
  BookOpen,
  BookText,
  ChartLine,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Code,
  Copy,
  Cpu,
  ExternalLink,
  FileText,
  Inbox,
  KeyRound,
  LayoutGrid,
  List,
  Lock,
  LogOut,
  Moon,
  MoreHorizontal,
  Percent,
  Plug,
  Scale,
  Search,
  Settings,
  ShieldCheck,
  Store,
  Sun,
  TriangleAlert,
  User,
  Users,
  Wallet,
  Webhook,
  X,
  Zap,
} from '../icons/index.tsx';

export type IconName =
  | 'grid'
  | 'swap'
  | 'wallet'
  | 'arrow'
  | 'key'
  | 'doc'
  | 'code'
  | 'webhook'
  | 'bolt'
  | 'book'
  | 'user'
  | 'shield'
  | 'percent'
  | 'lock'
  | 'bell'
  | 'users'
  | 'check'
  | 'ledger'
  | 'scale'
  | 'plug'
  | 'inbox'
  | 'cpu'
  | 'chart'
  | 'alert'
  | 'list'
  | 'gear'
  | 'search'
  | 'store'
  | 'sun'
  | 'moon'
  | 'logout'
  | 'dots'
  | 'ingot'
  | 'chevronDown'
  | 'chevronLeft'
  | 'chevronRight'
  | 'close'
  | 'copy';

const GLYPHS: Record<IconName, AppIcon> = {
  grid: LayoutGrid,
  swap: ArrowLeftRight,
  wallet: Wallet,
  arrow: ExternalLink,
  key: KeyRound,
  doc: FileText,
  code: Code,
  webhook: Webhook,
  bolt: Zap,
  book: BookOpen,
  user: User,
  shield: ShieldCheck,
  percent: Percent,
  lock: Lock,
  bell: Bell,
  users: Users,
  check: CircleCheck,
  ledger: BookText,
  scale: Scale,
  plug: Plug,
  inbox: Inbox,
  cpu: Cpu,
  chart: ChartLine,
  alert: TriangleAlert,
  list: List,
  gear: Settings,
  search: Search,
  store: Store,
  sun: Sun,
  moon: Moon,
  logout: LogOut,
  dots: MoreHorizontal,
  /* Lingotes da marca (ouro). */
  ingot: (props) => <CoinsIcon {...props} />,
  chevronDown: ChevronDown,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  close: X,
  copy: Copy,
};

export const ICON_NAMES = Object.keys(GLYPHS) as IconName[];

/**
 * Ícone do kit. Decorativo por default (`aria-hidden`): quem carrega o significado é o
 * texto ao lado. Sozinho num botão, passe `label`, e o botão ganha `aria-label`
 * (`spec/17` §7 item 12).
 */
export function Icon({
  name,
  size = 18,
  label,
  className,
}: {
  name: IconName;
  size?: number;
  label?: string;
  className?: string;
}) {
  const Glyph = GLYPHS[name];
  return <Glyph size={size} aria-label={label} className={cn('shrink-0', className)} />;
}
